import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, 
  CalendarDays, 
  TrendingUp, 
  Users,
  CheckCircle,
  AlertCircle,
  ClipboardCheck,
  User,
  UserCog,
  CalendarClock,
  FileCheck
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import PlanningValidationBlock from '../components/PlanningValidationBlock';
import WeatherWidget from '../components/WeatherWidget';
import NewsWidget from '../components/NewsWidget';
import TennisWidget from '../components/TennisWidget';
import ICalLink from '../components/ICalLink';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, icon: Icon, color = 'primary', subtext, onClick, className = '' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-emerald-500/10 text-emerald-500',
    warning: 'bg-amber-500/10 text-amber-500',
    danger: 'bg-red-500/10 text-red-500',
  };

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper 
      className={`stat-card animate-slide-up text-left w-full ${onClick ? 'cursor-pointer hover:border-primary transition-colors' : ''} ${className}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-label">{label}</p>
          <p className="stat-value">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Wrapper>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [hoursData, setHoursData] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dashboard widget preferences from user settings
  const showWeather = user?.show_weather !== false;
  const showNewsAltstaetten = user?.show_news_altstaetten !== false;
  const showTennis = user?.show_tennis !== false;
  
  // View mode: 'collaborateur' or 'manager'
  const [viewMode, setViewMode] = useState('collaborateur');
  const isManager = user?.role === 'manager' || user?.role === 'directeur';
  
  // Pending approvals
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [pendingPlannings, setPendingPlannings] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Always fetch personal stats for the connected user
        const personalRes = await axios.get(`${API}/stats/personal`);
        setStats(personalRes.data);

        // Get leave balance for the connected user
        const leaveRes = await axios.get(`${API}/leave-balance/${user.id}`);
        setLeaveBalance(leaveRes.data);

        // Managers/Directors also get team stats
        if (isManager) {
          const teamRes = await axios.get(`${API}/stats/team`);
          setTeamStats(teamRes.data);

          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
          
          const hoursRes = await axios.get(`${API}/stats/hours-by-user`, {
            params: { start_date: monthStart, end_date: monthEnd }
          });
          setHoursData(hoursRes.data.slice(0, 10));
          
          // Get pending leaves count
          try {
            const absencesRes = await axios.get(`${API}/absences`, { params: { status: 'pending' } });
            setPendingLeaves(absencesRes.data?.length || 0);
          } catch (e) {
            console.error('Error fetching pending leaves:', e);
          }
          
          // Get pending planning validations count
          try {
            const planningRes = await axios.get(`${API}/planning-validations/manager-view`, { 
              params: { status: 'pending' } 
            });
            setPendingPlannings(planningRes.data?.length || 0);
          } catch (e) {
            console.error('Error fetching pending plannings:', e);
          }
        }
      } catch (err) {
        toast.error(t('error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.role, user?.id, t, isManager]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl mb-1">{t('dashboard').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">
            {t('welcomeBack')}, {user?.first_name} — {t('yourSummary')}
          </p>
        </div>
        
        {/* View Mode Switch - Only for managers/directors */}
        {isManager && (
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
            <Button
              variant={viewMode === 'collaborateur' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('collaborateur')}
              className="flex items-center gap-2"
              data-testid="view-collaborateur-btn"
            >
              <User className="h-4 w-4" />
              Collaborateur
            </Button>
            <Button
              variant={viewMode === 'manager' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('manager')}
              className="flex items-center gap-2"
              data-testid="view-manager-btn"
            >
              <UserCog className="h-4 w-4" />
              Manager
            </Button>
          </div>
        )}
      </div>

      {/* ============ COLLABORATEUR VIEW ============ */}
      {(viewMode === 'collaborateur' || !isManager) && (
        <>
          {/* Planning Validation Block - For all users */}
          <PlanningValidationBlock />

          {/* iCal Calendar Sync */}
          <div className="flex justify-end">
            <ICalLink userId={user?.id} variant="compact" />
          </div>

          {/* Personal Stats - Hours with Variance - Hidden for managers (only visible for collaborateurs and directeurs) */}
          {user?.role !== 'manager' && (
          <div className="border border-border rounded-lg p-4 bg-card" data-testid="hours-summary-block">
            <h3 className="text-lg mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t('workHoursSummary') || 'RÉCAPITULATIF DES HEURES'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Week */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t('thisWeek') || 'Cette semaine'}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">{stats?.hours_this_week || 0}h</span>
                  <span className="text-muted-foreground">/ {stats?.hours_due_week || 0}h</span>
                </div>
                <div className={`text-sm font-medium ${(stats?.variance_week || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {t('variance') || 'Écart'}: {(stats?.variance_week || 0) >= 0 ? '+' : ''}{stats?.variance_week || 0}h
                </div>
                {stats?.overtime_week !== 0 && stats?.overtime_week !== undefined && (
                  <p className="text-xs text-purple-600">(dont H.Sup: {stats.overtime_week >= 0 ? '+' : ''}{stats.overtime_week}h)</p>
                )}
                {stats?.absence_days_week > 0 && (
                  <p className="text-xs text-muted-foreground">({stats.absence_days_week} j. d'absence)</p>
                )}
              </div>
              
              {/* Month */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t('thisMonth') || 'Ce mois'}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">{stats?.hours_this_month || 0}h</span>
                  <span className="text-muted-foreground">/ {stats?.hours_due_month || 0}h</span>
                </div>
                <div className={`text-sm font-medium ${(stats?.variance_month || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {t('variance') || 'Écart'}: {(stats?.variance_month || 0) >= 0 ? '+' : ''}{stats?.variance_month || 0}h
                </div>
                {stats?.overtime_month !== 0 && stats?.overtime_month !== undefined && (
                  <p className="text-xs text-purple-600">(dont H.Sup: {stats.overtime_month >= 0 ? '+' : ''}{stats.overtime_month}h)</p>
                )}
                {stats?.absence_days_month > 0 && (
                  <p className="text-xs text-muted-foreground">({stats.absence_days_month} j. d'absence)</p>
                )}
              </div>
              
              {/* Year */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t('thisYear') || 'Cette année'}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">{stats?.hours_this_year || 0}h</span>
                  <span className="text-muted-foreground">/ {stats?.hours_due_year || 0}h</span>
                </div>
                <div className={`text-sm font-medium ${(stats?.variance_year || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {t('variance') || 'Écart'}: {(stats?.variance_year || 0) >= 0 ? '+' : ''}{stats?.variance_year || 0}h
                </div>
                {stats?.overtime_year !== 0 && stats?.overtime_year !== undefined && (
                  <p className="text-xs text-purple-600">(dont H.Sup: {stats.overtime_year >= 0 ? '+' : ''}{stats.overtime_year}h)</p>
                )}
                {stats?.absence_days_year > 0 && (
                  <p className="text-xs text-muted-foreground">({stats.absence_days_year} j. d'absence)</p>
                )}
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
              <p>Base: {stats?.weekly_contractual_hours || 42.5}h/semaine ({stats?.daily_hours || 8.5}h/jour) • Heures dues = jours ouvrés passés - absences approuvées - jours fériés</p>
            </div>
          </div>
          )}

          {/* Absence Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              label={t('pendingAbsences')}
              value={stats?.pending_absences || 0}
              icon={AlertCircle}
              color="warning"
            />
            <StatCard
              label={t('approvedAbsences')}
              value={stats?.approved_absences || 0}
              icon={CheckCircle}
              color="success"
            />
          </div>

          {/* Leave Balance Card */}
          {leaveBalance && leaveBalance.allocated_days > 0 && (
            <div className="border border-border rounded-lg p-4 bg-card">
              <h3 className="text-lg mb-4">{t('leaveBalance').toUpperCase()} {new Date().getFullYear()}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('daysAllocated')}</p>
                  <p className="text-2xl font-bold" data-testid="leave-balance-allocated-days">
                    {leaveBalance.allocated_days} {t('days')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('daysUsed')}</p>
                  <p className="text-2xl font-bold text-emerald-600" data-testid="leave-balance-used-days">
                    {leaveBalance.used_days} {t('days')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('daysPlanned')}</p>
                  <p className="text-2xl font-bold text-amber-600" data-testid="leave-balance-planned-days">
                    {leaveBalance.planned_days} {t('days')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">{t('daysRemaining')}</p>
                  <p
                    className={`text-2xl font-bold ${leaveBalance.remaining_days < 0 ? 'text-red-500' : 'text-primary'}`}
                    data-testid="leave-balance-remaining-days"
                  >
                    {leaveBalance.remaining_days} {t('days')}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                  <div 
                    className="bg-emerald-500 h-full"
                    style={{ width: `${(leaveBalance.used_days / leaveBalance.allocated_days) * 100}%` }}
                  />
                  <div 
                    className="bg-amber-500 h-full"
                    style={{ width: `${(leaveBalance.planned_days / leaveBalance.allocated_days) * 100}%` }}
                  />
                </div>
              </div>
              {leaveBalance.day_hours && (
                <p className="text-xs text-muted-foreground mt-3" data-testid="leave-balance-day-hours">
                  1 {t('days')} = {leaveBalance.day_hours} {t('h')}
                </p>
              )}
            </div>
          )}

          {/* External Widgets Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {showWeather && <WeatherWidget />}
            {showNewsAltstaetten && <NewsWidget />}
            {showTennis && <TennisWidget />}
          </div>
        </>
      )}

      {/* ============ MANAGER VIEW ============ */}
      {viewMode === 'manager' && isManager && teamStats && (
        <>
          {/* Pending Approvals - Top Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              label="Congés à approuver"
              value={pendingLeaves}
              icon={CalendarClock}
              color={pendingLeaves > 0 ? 'warning' : 'success'}
              subtext={pendingLeaves > 0 ? 'Cliquez pour gérer' : 'Aucune demande en attente'}
              onClick={() => window.location.href = '/absences'}
              className={pendingLeaves > 0 ? 'border-amber-500/50' : ''}
            />
            <StatCard
              label="Plannings à valider"
              value={pendingPlannings}
              icon={FileCheck}
              color={pendingPlannings > 0 ? 'warning' : 'success'}
              subtext={pendingPlannings > 0 ? 'Cliquez pour gérer' : 'Aucune validation en attente'}
              onClick={() => window.location.href = '/reporting'}
              className={pendingPlannings > 0 ? 'border-amber-500/50' : ''}
            />
          </div>

          {/* Team Stats */}
          <div className="border-t border-border pt-6">
            <h2 className="text-xl mb-4">{t('teamStats').toUpperCase()}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                label={t('teamMembers')}
                value={teamStats.team_count}
                icon={Users}
              />
              <StatCard
                label={t('totalHoursMonth')}
                value={`${teamStats.total_hours_month}${t('h')}`}
                icon={Clock}
              />
              <StatCard
                label={t('avgPerPerson')}
                value={`${teamStats.avg_hours_per_person}${t('h')}`}
                icon={TrendingUp}
              />
              <StatCard
                label={t('pendingAbsences')}
                value={teamStats.pending_absences}
                icon={AlertCircle}
                color="warning"
              />
              <StatCard
                label={t('pendingCorrections')}
                value={teamStats.pending_corrections || 0}
                icon={ClipboardCheck}
                color="warning"
              />
            </div>
          </div>

          {/* Hours Chart */}
          {hoursData.length > 0 && (
            <div className="border border-border rounded-lg p-4 bg-card">
              <h3 className="text-lg mb-4">{t('hoursPerCollaborator').toUpperCase()}</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hoursData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis 
                      type="category" 
                      dataKey="user_name" 
                      width={100}
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 'var(--radius)'
                      }}
                      formatter={(value) => [`${value}${t('h')}`, t('hours')]}
                    />
                    <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border border-border rounded-lg p-4 bg-card">
            <h3 className="text-lg mb-4">{t('quickActions').toUpperCase()}</h3>
            <div className="flex flex-wrap gap-3">
              <a 
                href="/schedule" 
                className="btn-vitalis inline-flex items-center gap-2"
                data-testid="quick-action-schedule"
              >
                <CalendarDays className="h-4 w-4" />
                {t('viewSchedule')}
              </a>
              <a 
                href="/absences" 
                className="btn-vitalis inline-flex items-center gap-2"
                data-testid="quick-action-absences"
              >
                <Clock className="h-4 w-4" />
                {t('manageAbsences')}
              </a>
              <a 
                href="/reporting" 
                className="btn-vitalis inline-flex items-center gap-2"
                data-testid="quick-action-reporting"
              >
                <ClipboardCheck className="h-4 w-4" />
                Reporting Planning
              </a>
              <a 
                href="/corrections" 
                className="btn-vitalis inline-flex items-center gap-2"
                data-testid="quick-action-corrections"
              >
                <FileCheck className="h-4 w-4" />
                {t('pendingCorrections')}
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
